import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    Button,
    TextField,
    Alert,
    CircularProgress,
    Paper,
    Grid,
    Card,
    CardMedia,
    CardActions,
    IconButton,
    Divider
} from '@mui/material';
import { ArrowBack, Save, Delete, Upload } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import AdminHeader from '../components/AdminHeader';
import axios from 'axios';

const AboutUsManage = () => {
    const { user, isAuthenticated, loading:  authLoading } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        biographyTitle: '',
        biographyText: '',
        bodyLabTitle: '',
        bodyLabText: ''
    });
    const [diplomas, setDiplomas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (! authLoading && (! isAuthenticated || user?.role !== 'ADMIN')) {
            navigate('/login');
        }
    }, [isAuthenticated, user, authLoading, navigate]);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            // Fetch About Us data
            const aboutRes = await axios.get(`${process.env.REACT_APP_API_URL}/admin/about-us`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFormData(aboutRes.data);

            // Fetch Diplomas
            const diplomasRes = await axios.get(`${process.env.REACT_APP_API_URL}/admin/about-us/diplomas`, {
                headers: { Authorization:  `Bearer ${token}` }
            });
            setDiplomas(diplomasRes.data);
        } catch (err) {
            console.error(err);
            setError('Грешка при вчитување');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            await axios.put(
                `${process.env.REACT_APP_API_URL}/admin/about-us`,
                formData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSuccess('Содржината е успешно зачувана! ');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error(err);
            setError('Грешка при зачувување');
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleUploadDiploma = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const data = new FormData();
            data.append('image', file);

            await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/about-us/diplomas`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            setSuccess('Дипломата е успешно додадена!');
            fetchData(); // Refresh
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error(err);
            setError('Грешка при upload');
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDiploma = async (id) => {
        if (! window.confirm('Дали сте сигурни? ')) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            await axios.delete(`${process.env.REACT_APP_API_URL}/admin/about-us/diplomas/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setSuccess('Дипломата е избришана!');
            fetchData(); // Refresh
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error(err);
            setError('Грешка при бришење');
            setTimeout(() => setError(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: 'background.default' }}>
            <Container maxWidth="lg">
                <AdminHeader />

                <Box sx={{ mb: 4 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/admin/sections')}
                        sx={{ mb: 3 }}
                    >
                        Назад кон секции
                    </Button>

                    <Typography variant="h3" sx={{ fontWeight: 'bold', mb:  4 }}>
                        Уреди "За Нас"
                    </Typography>
                </Box>

                {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {loading && ! formData.biographyTitle ?  (
                    <Box display="flex" justifyContent="center" py={8}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Biography Section */}
                        <Paper sx={{ p: 4, mb:  4 }}>
                            <Typography variant="h5" gutterBottom fontWeight="bold">
                                Биографија
                            </Typography>
                            <TextField
                                label="Наслов"
                                fullWidth
                                value={formData.biographyTitle}
                                onChange={(e) => setFormData({...formData, biographyTitle: e. target.value})}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Текст"
                                multiline
                                rows={10}
                                fullWidth
                                value={formData.biographyText}
                                onChange={(e) => setFormData({... formData, biographyText:  e.target.value})}
                            />
                        </Paper>

                        {/* BodyLab Section */}
                        <Paper sx={{ p: 4, mb: 4 }}>
                            <Typography variant="h5" gutterBottom fontWeight="bold">
                                BodyLab by Anamarija
                            </Typography>
                            <TextField
                                label="Наслов"
                                fullWidth
                                value={formData.bodyLabTitle}
                                onChange={(e) => setFormData({...formData, bodyLabTitle: e. target.value})}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Текст"
                                multiline
                                rows={10}
                                fullWidth
                                value={formData.bodyLabText}
                                onChange={(e) => setFormData({...formData, bodyLabText: e.target.value})}
                            />
                        </Paper>

                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Save />}
                            onClick={handleSave}
                            disabled={loading}
                            sx={{ mb: 6 }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Зачувај Промени'}
                        </Button>

                        <Divider sx={{ my: 4 }} />

                        {/* Diplomas Section */}
                        <Paper sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb:  3 }}>
                                <Typography variant="h5" fontWeight="bold">
                                    Дипломи ({diplomas.length})
                                </Typography>
                                <Button
                                    variant="contained"
                                    component="label"
                                    startIcon={<Upload />}
                                >
                                    Додај Диплома
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleUploadDiploma}
                                    />
                                </Button>
                            </Box>

                            <Grid container spacing={3}>
                                {diplomas.map((diploma) => (
                                    <Grid item xs={12} sm={6} md={4} key={diploma.id}>
                                        <Card>
                                            <CardMedia
                                                component="img"
                                                height="200"
                                                image={`${process.env.REACT_APP_API_URL}${diploma.imageUrl}`}
                                                alt={diploma.fileName}
                                            />
                                            <CardActions>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDeleteDiploma(diploma.id)}
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>

                            {diplomas.length === 0 && (
                                <Typography variant="body1" color="text.secondary" textAlign="center" py={4}>
                                    Нема додадени дипломи.
                                </Typography>
                            )}
                        </Paper>
                    </>
                )}
            </Container>
        </Box>
    );
};

export default AboutUsManage;